import { DatePicker, Select, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  cancelGuestReservation,
  getGuestReservations,
  updateGuestContactStatus,
} from '../../../api/guestReservation.api';

import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import Pagination from '../../../components/Pagination';
import SelectCampus from '../../../components/SelectCampus';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import config from '../../../lib/config';
import exportGuestReservation from '../../../lib/exportGuestReservation';
import { getCommonCodeLabel } from '../../../lib/store';
import { addHyphen } from '../../../lib/util';
import { RootState } from '../../../store/rootReducer';
import { ISelect } from '../../../models/common.model';
import {
  IGetGuestReservations,
  IGuestReservationItem,
} from '../../../models/guestReservation.model';
const { Option } = Select;

const Wrapper = styled.div`
  .guest-contact-absence .ant-select-selector {
    background-color: #fbe3e6;
    border-color: #f2c9ce;
  }
  .guest-contact-absence .ant-select-selection-item {
    color: #c0392b;
  }
  .ox {
    font-weight: 800;
  }
  .ox.o {
    color: #0a8f5b;
  }
  .ox.x {
    color: #b9bfc9;
  }
  .dttm-cell {
    cursor: pointer;
  }
  .parent-name {
    font-weight: 700;
  }
  .ant-table-tbody > tr.row-canceled {
    opacity: 0.5;
  }
`;

const renderOx = (value: string) =>
  value === 'Y' ? (
    <span className="ox o">O</span>
  ) : (
    <span className="ox x">X</span>
  );

// 칸이 좁아 화면에는 일까지만 찍고 시·분은 title 로 넘긴다.
// yn='Y' 인데 시각이 없으면(정식 회원가입 또는 컬럼 추가 전 승격) O 를 찍는다.
const renderOxDate = (yn: string, dttm: string | null) => {
  if (yn !== 'Y') return <span className="ox x">X</span>;
  if (!dttm) return <span className="ox o">O</span>;
  return (
    <span className="dttm-cell" title={moment(dttm).format('YYYY.MM.DD HH:mm')}>
      {moment(dttm).format('YYYY.MM.DD')}
    </span>
  );
};

const GuestReservation = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { successNotification, errorNotification } = useNotification();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  // 시스템관리자(본사)만 전체 캠퍼스 검색 가능
  const isHq = auth?.hqYn === 'Y';

  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetGuestReservations>();

  const [campusCode, setCampusCode] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchName, setSearchName] = useState<string>('');
  const [page, setPage] = useState(1);
  // 마지막으로 검색한 파라미터 (연락상태 변경 후 동일 조건으로 재조회할 때 사용)
  const [lastParam, setLastParam] = useState<any>({});

  const [cancelVisible, setCancelVisible] = useState(false);
  const [cancelRecord, setCancelRecord] = useState<IGuestReservationItem>();

  // 연락상태코드 (GM0001)
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const [contactStatusList, setContactStatusList] = useState<ISelect[]>([]);

  useEffect(() => {
    const GM0001 = commonCodes.find(
      (item) => item.commonCode === 'GM0001',
    )?.detailCode;

    if (GM0001) {
      const _list: any = [];
      const _GM0001 = [...GM0001].sort((a, b) =>
        a.orderSeq < b.orderSeq ? -1 : a.orderSeq > b.orderSeq ? 1 : 0,
      );
      _GM0001.forEach((item: any) => {
        _list.push({
          label: item.korName,
          value: item.detailCode,
        });
      });
      setContactStatusList(_list);
    }
  }, [commonCodes]);

  const doSearch = (overrides?: {
    startDate?: string;
    endDate?: string;
    campusCode?: string;
  }) => {
    const _startDate = overrides?.startDate ?? startDate;
    const _endDate = overrides?.endDate ?? endDate;
    let _campusCode = overrides?.campusCode ?? campusCode;
    // 캠퍼스 계정은 전체 검색 불가 — 캠퍼스 미지정 시 자기 캠퍼스로 고정
    if (!isHq && !_campusCode) _campusCode = String(currentCampus || '');

    const _param: any = {};
    if (_campusCode) _param.campusCode = _campusCode;
    if (_startDate) _param.startDate = _startDate;
    if (_endDate) _param.endDate = _endDate;
    if (searchName) _param.searchName = searchName;

    setPage(1);
    setLastParam(_param);
    execute(getGuestReservations(_param));
  };

  const search = () => doSearch();

  // 검색 조건·페이지를 유지한 채 목록만 갱신 (기존 데이터를 비우지 않아 깜빡임 없음)
  const reload = () => {
    execute(getGuestReservations(lastParam), false);
  };

  useEffect(() => {
    if (serverDate && auth) {
      const _startDate = moment(serverDate).subtract(1, 'month').format('YYYYMMDD');
      const _endDate = moment(serverDate).format('YYYYMMDD');
      setStartDate(_startDate);
      setEndDate(_endDate);

      // 캠퍼스 계정은 자기 캠퍼스를 기본 선택
      let _campusCode = campusCode;
      if (!isHq) {
        _campusCode = String(currentCampus || '');
        setCampusCode(_campusCode);
      }

      doSearch({
        startDate: _startDate,
        endDate: _endDate,
        campusCode: _campusCode,
      });
    }
  }, [serverDate, auth]);

  const onChangeContactStatus = async (
    record: IGuestReservationItem,
    value: string,
  ) => {
    try {
      await updateGuestContactStatus(record.resvCode, {
        contactStatusCode: value,
      });
      successNotification(t('common.message.save1'));
      reload();
    } catch (e) {
      console.error(e);
    }
  };

  const onCancelReservation = async () => {
    if (!cancelRecord) return;

    try {
      await cancelGuestReservation(cancelRecord.resvCode);
      successNotification(t('common.message.cancel'));
      reload();
    } catch (e: any) {
      errorNotification(e?.response?.data?.data || t('common.message.error'));
    } finally {
      setCancelVisible(false);
      setCancelRecord(undefined);
    }
  };

  const onDownloadExcel = () => {
    if (res?.data) exportGuestReservation(res.data);
  };

  const columns: TableColumnsType<IGuestReservationItem> = [
    {
      title: '번호',
      dataIndex: 'resvCode',
      key: 'resvCode',
      align: 'center',
      sorter: (a, b) => a.resvCode - b.resvCode,
    },
    {
      title: '캠퍼스명',
      dataIndex: 'campusName',
      key: 'campusName',
      align: 'center',
    },
    {
      title: '문의 시간',
      dataIndex: 'firstRegDttm',
      key: 'firstRegDttm',
      align: 'center',
      sorter: (a, b) => a.firstRegDttm?.localeCompare(b.firstRegDttm),
      render: (value) => (value ? moment(value).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '학생명',
      dataIndex: 'studentName',
      key: 'studentName',
      align: 'center',
      sorter: (a, b) => a.studentName?.localeCompare(b.studentName),
      render: (value) => value ?? '-',
    },
    {
      title: '학교명',
      dataIndex: 'schoolName',
      key: 'schoolName',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
      render: (value) => value ?? '-',
    },
    {
      title: '학년',
      dataIndex: 'gradeCode',
      key: 'gradeCode',
      align: 'center',
      sorter: (a, b) =>
        getCommonCodeLabel('SY0006', a.gradeCode, 'ko')?.localeCompare(
          getCommonCodeLabel('SY0006', b.gradeCode, 'ko'),
        ),
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: '학부모명',
      dataIndex: 'parentName',
      key: 'parentName',
      align: 'center',
      sorter: (a, b) => a.parentName?.localeCompare(b.parentName),
      render: (value) => <span className="parent-name">{value ?? '-'}</span>,
    },
    {
      title: '학부모 연락처',
      dataIndex: 'parentMobilePhone',
      key: 'parentMobilePhone',
      align: 'center',
      render: (value) => (value ? addHyphen(value) : '-'),
    },
    {
      title: '마케팅 동의',
      dataIndex: 'marketingAgreeYn',
      key: 'marketingAgreeYn',
      align: 'center',
      sorter: (a, b) => a.marketingAgreeYn?.localeCompare(b.marketingAgreeYn),
      render: renderOx,
    },
    {
      title: '입학테스트 희망일',
      dataIndex: 'examDate',
      key: 'examDate',
      align: 'center',
      sorter: (a, b) => a.examDate?.localeCompare(b.examDate),
      render: (value, record) =>
        value
          ? `${moment(value, 'YYYYMMDD').format('YYYY.MM.DD')} ${record.examTime || ''}`.trim()
          : '-',
    },
    {
      title: '학부모 연락',
      dataIndex: 'contactStatusCode',
      key: 'contactStatusCode',
      align: 'center',
      render: (value, record) => {
        return (
          <Select
            value={value || undefined}
            style={{ minWidth: 100 }}
            className={value === 'NA' ? 'guest-contact-absence' : ''}
            onChange={(v) => onChangeContactStatus(record, v)}
            disabled={writeAuthYn === 'N' || record.cancelYn === 'Y'}
          >
            {contactStatusList.map((item) => (
              <Option key={item.value} value={item.value}>
                {item.label}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: '입학시험 응시',
      dataIndex: 'attendYn',
      key: 'attendYn',
      align: 'center',
      sorter: (a, b) => a.attendYn?.localeCompare(b.attendYn),
      render: (value, record) => renderOxDate(value, record.attendDttm),
    },
    {
      title: '회원가입',
      dataIndex: 'memberJoinYn',
      key: 'memberJoinYn',
      align: 'center',
      sorter: (a, b) => a.memberJoinYn?.localeCompare(b.memberJoinYn),
      render: (value, record) => renderOxDate(value, record.memberJoinDttm),
    },
    {
      title: '취소',
      key: 'cancel',
      align: 'center',
      render: (_value, record) => {
        if (record.cancelYn === 'Y') return <span className="text-gray">취소</span>;
        const blocked =
          record.attendYn === 'Y' ||
          record.paymentCompleteYn === 'Y' ||
          record.classRegisteredYn === 'Y';
        if (blocked) return '-';
        return (
          <div className="comm-btn__area justify-content-center">
            <Button
              className="btn-basic small mint"
              onClick={() => {
                setCancelRecord(record);
                setCancelVisible(true);
              }}
              text="취소"
              disabled={writeAuthYn === 'N'}
              writeAuthYn={writeAuthYn}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Wrapper>
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('homepage.landing.title1')}
        currentMenu="비회원 입학시험 예약"
        onlyTitle={true}
      />
      <div className="common-search">
        <div className="search-area">
          <ul className="search-bx">
            <li>
              <label>{t('common.menu.campus')}</label>
              <div>
                <SelectCampus
                  hasAll={isHq}
                  value={campusCode}
                  onChange={(value: string) => setCampusCode(value)}
                />
              </div>
            </li>
            <li>
              <label>{t('common.excel.period')}</label>
              <div className="date-picker__area">
                <DatePicker
                  value={startDate ? moment(startDate, 'YYYYMMDD') : undefined}
                  onChange={(e) => {
                    if (e === null) setStartDate('');
                    else setStartDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
              ~
              <div className="date-picker__area">
                <DatePicker
                  value={endDate ? moment(endDate, 'YYYYMMDD') : undefined}
                  onChange={(e) => {
                    if (e === null) setEndDate('');
                    else setEndDate(moment(e).format('YYYYMMDD'));
                  }}
                  format={['YYYY-MM-DD', 'YYYYMMDD']}
                />
              </div>
            </li>
            <li>
              <input
                type="text"
                className="form-control w-240p"
                placeholder="학부모명 / 학생명 입력"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') search();
                }}
              />
              <div className="comm-btn__area">
                <Button
                  className="btn-basic small black"
                  onClick={search}
                  text={t('common.button.search')}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div className="content scrollbar">
        {isLoading ? <Loader /> : null}
        <ErrorResponse error={error} />

        <div className="col-frame">
          <div className="comm-bx__between">
            <div className="comm-sch_result">
              {t('common.text.count')}: <b>{res?.data.length || 0}</b>
              {t('common.text.unit')}
            </div>
            <div className="col-6 text-right" />
          </div>
          <div className="comm-table__area">
            <Table
              columns={columns}
              dataSource={res?.data}
              pagination={{
                current: page,
                pageSize: config.middlePageSize,
              }}
              rowKey="resvCode"
              rowClassName={(record) =>
                record.cancelYn === 'Y' ? 'row-canceled' : ''
              }
            />
          </div>
          <div className="comm-bx__between">
            <div className="comm-btn__area">
              <Button
                className="btn-basic green"
                onClick={onDownloadExcel}
                text="Download"
                disabled={!res?.data.length}
                writeAuthYn={writeAuthYn}
                useExcelIcon
              />
            </div>
            <div className="common-paging center">
              <Pagination
                count={res?.data.length || 0}
                page={page}
                setPage={setPage}
                perPage={config.middlePageSize}
              />
            </div>
          </div>
        </div>
      </div>
      {cancelVisible && (
        <ConfirmModal
          visible={cancelVisible}
          setVisible={setCancelVisible}
          onConfirm={onCancelReservation}
          titleText="예약 취소"
          messageText="이 예약을 취소하시겠습니까? 입학시험 신청도 함께 취소되며 되돌릴 수 없습니다."
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
        />
      )}
    </Wrapper>
  );
};

export default GuestReservation;
